import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { coursesApi, Course } from "../lib";
import { showError } from "../lib/toast";
import SEO from "../components/SEO";
import {
  Search,
  Star,
  Clock,
  Users,
  BookOpen,
  ArrowRight,
  ChevronDown,
  GraduationCap,
  Sparkles,
  Trophy,
  X,
  Play,
  Award,
  ChevronLeft,
  ChevronRight,
  Loader,
  Filter,
  Shield,
  Lock,
  Zap,
  Check,
  TrendingUp,
  Target,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Types
interface CourseCardData {
  _id: string;
  title: string;
  thumbnail: string;
  price: number;
  originalPrice?: number;
  totalLessons: number;
  enrolledStudents: number;
  rating: number;
  reviewsCount: number;
  category: string;
  difficulty: "beginner" | "intermediate" | "advanced" | "All Levels";
  totalDuration: number;
  instructor: {
    _id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  shortDescription: string;
  tags: string[];
  isFeatured?: boolean;
  isNew?: boolean;
  certificate: boolean;
  language: string;
}

// Helpers
const fmtPrice = (p: number) =>
  new Intl.NumberFormat("fr-CM", {
    style: "currency",
    currency: "XAF",
    maximumFractionDigits: 0,
  }).format(p);

const levelColor = (level: string) => {
  switch (level) {
    case "Beginner":
      return "bg-green-100 text-green-700";
    case "Intermediate":
      return "bg-blue-100 text-blue-700";
    case "Advanced":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const RatingStars = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-2.5 h-2.5 md:w-3 md:h-3 ${s <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
      />
    ))}
  </div>
);

// Course Card Component
const CourseCard = ({ course }: { course: CourseCardData }) => {
  const level = mapDifficultyToLevel(course.difficulty);
  const instructorName = course.instructor
    ? `${course.instructor.firstName || ""} ${course.instructor.lastName || ""}`.trim()
    : "Instructor";

  const formatDuration = (totalDuration: number) => {
    const hours = Math.floor(totalDuration / 60);
    const minutes = totalDuration % 60;
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  return (
    <Link
      to={`/course/${course._id}`}
      className="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-200 group rounded-xl hover:shadow-lg hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-gray-100 h-36 sm:h-40 md:h-44">
        <img
          src={
            course.thumbnail ||
            "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop"
          }
          alt={course.title}
          className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Badges */}
        <div className="absolute flex flex-col gap-0.5 top-2 left-2">
          {course.isFeatured && (
            <span className="text-[8px] md:text-[9px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded">
              Bestseller
            </span>
          )}
          {course.isNew && !course.isFeatured && (
            <span className="text-[8px] md:text-[9px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded">
              New
            </span>
          )}
        </div>

        <div className="absolute top-2 right-2">
          <span
            className={`text-[8px] md:text-[9px] font-semibold px-1.5 py-0.5 rounded ${levelColor(level)}`}
          >
            {level}
          </span>
        </div>

        {/* Price */}
        <div className="absolute flex items-center gap-0.5 bottom-2 right-2">
          {course.originalPrice && course.originalPrice > course.price && (
            <span className="text-[7px] md:text-[8px] text-white/60 line-through">
              {fmtPrice(course.originalPrice)}
            </span>
          )}
          <span className="text-[9px] md:text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded">
            {course.price === 0 ? "Free" : fmtPrice(course.price)}
          </span>
        </div>

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 group-hover:opacity-100">
          <div className="flex items-center justify-center w-8 h-8 rounded-full shadow-lg bg-white/90 md:w-10 md:h-10">
            <Play className="w-3 h-3 text-gray-900 ml-0.5 md:w-4 md:h-4" />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-3 md:p-4">
        <h3 className="text-[11px] md:text-sm font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors">
          {course.title}
        </h3>

        {/* Instructor */}
        <div className="flex items-center gap-1 mb-1.5 md:mb-2">
          {course.instructor?.avatar ? (
            <img
              src={course.instructor.avatar}
              alt={instructorName}
              className="object-cover w-4 h-4 rounded-full md:w-5 md:h-5"
            />
          ) : (
            <div className="flex items-center justify-center w-4 h-4 bg-gray-200 rounded-full md:w-5 md:h-5">
              <span className="text-[8px] font-bold text-gray-500 md:text-[10px]">
                {instructorName[0]}
              </span>
            </div>
          )}
          <span className="text-[9px] text-gray-500 truncate md:text-xs">
            {instructorName}
          </span>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-2 mb-2 text-[9px] text-gray-500 md:text-xs md:gap-3">
          <div className="flex items-center gap-0.5">
            <BookOpen className="w-2.5 h-2.5 md:w-3 md:h-3" />
            <span>{course.totalLessons} lessons</span>
          </div>
          <div className="flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5 md:w-3 md:h-3" />
            <span>{formatDuration(course.totalDuration)}</span>
          </div>
          {course.certificate && (
            <div className="flex items-center gap-0.5 text-amber-500">
              <Award className="w-2.5 h-2.5 md:w-3 md:h-3" />
              <span>Cert</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 mt-auto border-t border-gray-100">
          <div className="flex items-center gap-1">
            <RatingStars rating={course.rating} />
            <span className="text-[9px] font-bold text-gray-900 md:text-xs">
              {course.rating.toFixed(1)}
            </span>
            <span className="text-[7px] text-gray-400 md:text-[9px]">
              ({course.reviewsCount.toLocaleString()})
            </span>
          </div>
          <div className="flex items-center gap-0.5 text-[9px] text-gray-400 md:text-xs">
            <Users className="w-2.5 h-2.5 md:w-3 md:h-3" />
            <span>{course.enrolledStudents.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Main Page
const CoursesPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setCategory] = useState("All");
  const [selectedLevel, setLevel] = useState("All");
  const [sortBy, setSortBy] = useState("popular");

  const [courses, setCourses] = useState<CourseCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const limit = 8;

  // Features array for hero section
  const features = [
    { icon: Lock, text: "Hands-on labs with real-world scenarios" },
    { icon: Zap, text: "Live threat detection exercises" },
    { icon: Users, text: "1-on-1 mentorship from professionals" },
    { icon: Check, text: "Career support and job placement" },
  ];

  const categories = [
    "All",
    "Design",
    "Development",
    "Data Science",
    "Marketing",
    "Business",
    "Photography",
    "Music",
  ];
  const levels = ["All", "Beginner", "Intermediate", "Advanced"];
  const sortOptions = [
    { value: "popular", label: "Most Popular" },
    { value: "newest", label: "Newest" },
    { value: "rating", label: "Highest Rated" },
    { value: "price", label: "Price: Low to High" },
  ];

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const params: any = { page: currentPage, limit, isPublished: true };
        if (selectedCategory !== "All") params.category = selectedCategory;
        if (selectedLevel !== "All")
          params.difficulty = selectedLevel.toLowerCase();
        if (searchQuery) params.search = searchQuery;

        switch (sortBy) {
          case "newest":
            params.sortBy = "createdAt";
            params.sortOrder = "desc";
            break;
          case "rating":
            params.sortBy = "rating";
            params.sortOrder = "desc";
            break;
          case "price":
            params.sortBy = "price";
            params.sortOrder = "asc";
            break;
          default:
            params.sortBy = "enrolledStudents";
            params.sortOrder = "desc";
        }

        const response = await coursesApi.getCourses(params);
        if (response.success) {
          setCourses(response.data);
          setTotalPages(response.pagination.pages);
          setTotalCourses(response.pagination.total);
        }
      } catch (err) {
        console.error("Failed to fetch courses:", err);
        showError("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [currentPage, selectedCategory, selectedLevel, sortBy, searchQuery]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedLevel, sortBy, searchQuery]);

  const activeFilterCount =
    (selectedCategory !== "All" ? 1 : 0) +
    (selectedLevel !== "All" ? 1 : 0) +
    (searchQuery ? 1 : 0);

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".course-card",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.04,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".courses-grid",
            start: "top 90%",
            toggleActions: "play none none none",
          },
        },
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="Online Tech Courses - Web Development, Data Science & Cybersecurity"
        description="Master in-demand skills with 200+ expert-led courses in web development, data science, cybersecurity, UI/UX design, and business."
        keywords="online courses, web development, data science, cybersecurity, UI/UX design"
      />
      <main className="min-h-screen my-10 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        {/* Hero Section */}
        <section className="relative py-8 overflow-hidden md:py-12 lg:py-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 mb-4 bg-gradient-to-r from-orange-500/10 to-orange-600/10 rounded-full border border-orange-200/30">
                <Sparkles className="w-3 h-3 text-orange-500 animate-pulse" />
                <span className="text-[9px] font-semibold text-orange-600">200+ Expert Courses</span>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
                <span className="text-transparent bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text">
                  LearnHub
                </span>{" "}
                Courses
              </h1>
              <p className="max-w-2xl mx-auto mt-3 text-sm text-gray-600 sm:text-base">
                Master in-demand skills with expert-led courses in web development, 
                data science, cybersecurity, and more. Get certified and advance your career.
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-6 mt-6 md:mt-8">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-100">
                  <Users className="w-3.5 h-3.5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">50,000+</p>
                  <p className="text-[9px] text-gray-500">Students</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-100">
                  <BookOpen className="w-3.5 h-3.5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">200+</p>
                  <p className="text-[9px] text-gray-500">Courses</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-100">
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">4.8</p>
                  <p className="text-[9px] text-gray-500">Rating</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filters */}
        <div className="z-20 py-3 border-b border-gray-200 bg-white/95 backdrop-blur-sm md:py-4">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 md:flex-row">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute w-3.5 h-3.5 text-gray-400 -translate-y-1/2 left-3 top-1/2 md:w-4 md:h-4" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:py-2.5 md:text-sm md:rounded-xl"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  </button>
                )}
              </div>

              {/* Desktop Filters */}
              <div className="hidden gap-2 md:flex">
                <select
                  value={selectedCategory}
                  onChange={(e) => setCategory(e.target.value)}
                  className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm md:rounded-xl"
                >
                  {categories.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={selectedLevel}
                  onChange={(e) => setLevel(e.target.value)}
                  className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm md:rounded-xl"
                >
                  {levels.map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm md:rounded-xl"
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="md:hidden flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${showMobileFilters ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            {/* Mobile Filters Panel */}
            {showMobileFilters && (
              <div className="p-3 mt-3 space-y-2 bg-white border border-gray-200 rounded-lg md:hidden">
                <div>
                  <label className="block mb-0.5 text-[10px] font-medium text-gray-700">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50"
                  >
                    {categories.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-0.5 text-[10px] font-medium text-gray-700">Level</label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setLevel(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50"
                  >
                    {levels.map((l) => (
                      <option key={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-0.5 text-[10px] font-medium text-gray-700">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50"
                  >
                    {sortOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Active Filters */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2 mt-2 border-t border-gray-100">
                <span className="text-[9px] text-gray-500">Active filters:</span>
                {selectedCategory !== "All" && (
                  <button
                    onClick={() => setCategory("All")}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] text-orange-700 rounded-full bg-orange-50"
                  >
                    {selectedCategory} <X className="w-2.5 h-2.5" />
                  </button>
                )}
                {selectedLevel !== "All" && (
                  <button
                    onClick={() => setLevel("All")}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] text-purple-700 rounded-full bg-purple-50"
                  >
                    {selectedLevel} <X className="w-2.5 h-2.5" />
                  </button>
                )}
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] text-gray-600 rounded-full bg-gray-100"
                  >
                    "{searchQuery.length > 15 ? searchQuery.slice(0, 15) + "…" : searchQuery}" <X className="w-2.5 h-2.5" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setCategory("All");
                    setLevel("All");
                    setSearchQuery("");
                  }}
                  className="ml-auto text-[9px] text-red-500 hover:text-red-600"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Category Quick Filters */}
        <div className="px-4 mx-auto mt-4 max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 pb-1 overflow-x-auto no-scrollbar">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap md:text-xs md:px-3 md:py-1.5 ${
                  selectedCategory === cat
                    ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="px-4 mx-auto mt-4 max-w-7xl sm:px-6 lg:px-8">
          {!loading && (
            <p className="text-[10px] text-gray-500 md:text-xs">
              Showing <span className="font-semibold text-gray-900">{courses.length}</span> of{" "}
              <span className="font-semibold text-gray-900">{totalCourses}</span> courses
              {selectedCategory !== "All" && (
                <span> in <span className="text-orange-600">{selectedCategory}</span></span>
              )}
            </p>
          )}
        </div>

        {/* Courses Grid */}
        <div className="px-4 mx-auto mt-4 max-w-7xl sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : courses.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 courses-grid">
              {courses.map((course) => (
                <CourseCard key={course._id} course={course} />
              ))}
            </div>
          ) : (
            <div className="py-12 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900">No courses found</h3>
              <p className="mb-4 text-[10px] text-gray-500 max-w-xs mx-auto">
                Try adjusting your search terms or clearing filters
              </p>
              <button
                onClick={() => {
                  setCategory("All");
                  setLevel("All");
                  setSearchQuery("");
                }}
                className="px-4 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
              >
                Reset all filters
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="px-4 pb-12 mx-auto mt-6 max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed md:p-2"
                >
                  <ChevronLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-6 h-6 text-[10px] font-medium rounded-lg transition-colors md:w-8 md:h-8 md:text-sm ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed md:p-2"
                >
                  <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </button>
              </div>
            </div>
            <p className="mt-2 text-[9px] text-center text-gray-400 md:text-xs">
              Page {currentPage} of {totalPages}
            </p>
          </div>
        )}
      </main>
    </>
  );
};

function mapDifficultyToLevel(difficulty: string) {
  const map: Record<string, string> = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    "All Levels": "All Levels",
  };
  return map[difficulty?.toLowerCase()] || "All Levels";
}

export default CoursesPage;